// src/gestion-logistics/components/JobCostingTab.tsx
import React, { useState, useEffect } from "react";
import {
  Button,
  Space,
  Popconfirm,
  Tag,
  Tooltip,
  Alert,
  Empty,
  theme,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RiseOutlined,
  FallOutlined,
  LockOutlined,
  DollarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import type { ColumnType } from "antd/es/table";
import { useJobCosting } from "../hooks/useJobCosting";
import { useLogisticsPermissions } from "../hooks/useLogisticsPermissions";
import { JobCostingLine, CostLineType } from "../types/costing.types";
import JobCostingLineFormModal from "./JobCostingLineFormModal";
import FinancialSummaryCard from "./FinancialSummaryCard";
import InvoiceGeneratorModal from "./InvoiceGeneratorModal";
import SupplierBillRecordModal from "./SupplierBillRecordModal";
import InvoiceDetailModal from "./InvoiceDetailModal";
import SupplierBillDetailModal from "./SupplierBillDetailModal";
import { isSupplierBillableLine, isCostingLineLocked } from "../utils/costingBilling";

import { ShipmentDataTable, ShipmentTabHeading } from "./ShipmentDetailLayout";

interface JobCostingTabProps {
  shipmentId: number;
  currencySymbol?: string;
  onRefreshNeeded?: () => void;
}

/**
 * Onglet Job Costing - Affiche les lignes de coûts et revenus
 */
const JobCostingTab: React.FC<JobCostingTabProps> = ({
  shipmentId,
  currencySymbol = "",
  onRefreshNeeded,
}) => {
  const {
    jobCostingLines,
    summary,
    loading,
    fetchJobCostingByShipment,
    fetchJobCostingSummary,
    deleteJobCostingLine,
  } = useJobCosting();

  const permissions = useLogisticsPermissions();
  const { token } = theme.useToken();

  const [formModalVisible, setFormModalVisible] = useState(false);
  const [selectedLine, setSelectedLine] = useState<JobCostingLine | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [invoiceGeneratorVisible, setInvoiceGeneratorVisible] = useState(false);
  const [supplierBillRecordVisible, setSupplierBillRecordVisible] =
    useState(false);

  // Modaux de détails des factures
  const [invoiceDetailVisible, setInvoiceDetailVisible] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null
  );
  const [supplierBillDetailVisible, setSupplierBillDetailVisible] =
    useState(false);
  const [selectedSupplierBill, setSelectedSupplierBill] = useState<any>(null);

  useEffect(() => {
    if (shipmentId) {
      fetchJobCostingByShipment(shipmentId);
      fetchJobCostingSummary(shipmentId);
    }
  }, [shipmentId, fetchJobCostingByShipment, fetchJobCostingSummary]);

  const handleCreate = () => {
    setSelectedLine(null);
    setFormMode("create");
    setFormModalVisible(true);
  };

  const handleEdit = (record: JobCostingLine) => {
    setSelectedLine(record);
    setFormMode("edit");
    setFormModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    await deleteJobCostingLine(id);
    // Auto-refresh après suppression
    if (shipmentId) {
      fetchJobCostingByShipment(shipmentId);
      fetchJobCostingSummary(shipmentId);
      onRefreshNeeded?.();
    }
  };

  const handleCloseModal = () => {
    setFormModalVisible(false);
    setSelectedLine(null);
    // Auto-refresh après création/modification
    if (shipmentId) {
      fetchJobCostingByShipment(shipmentId);
      fetchJobCostingSummary(shipmentId);
      onRefreshNeeded?.();
    }
  };

  const formatCurrency = (amount: number | string | null) => {
    // Gérer currencySymbol null/undefined pour éviter affichage "null"
    const symbol = currencySymbol || "";

    if (amount === null || amount === undefined) {
      return symbol ? `0 ${symbol}` : "0";
    }

    const numericAmount =
      typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) {
      return symbol ? `0 ${symbol}` : "0";
    }

    const formatted = numericAmount.toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return symbol ? `${formatted} ${symbol}` : formatted;
  };

  const columns: ColumnType<JobCostingLine>[] = [
    {
      title: "Type",
      dataIndex: "line_type",
      key: "line_type",
      width: 100,
      fixed: "left",
      align: "center",
      render: (type: CostLineType) => {
        if (type === CostLineType.COST) {
          return (
            <Tag icon={<FallOutlined aria-hidden="true" />} color="orange">
              Coût
            </Tag>
          );
        } else if (type === CostLineType.DISBURSEMENT) {
          return (
            <Tag icon={<DollarOutlined aria-hidden="true" />} color="blue">
              Débours
            </Tag>
          );
        } else {
          return (
            <Tag icon={<RiseOutlined aria-hidden="true" />} color="green">
              Revenu
            </Tag>
          );
        }
      },
    },
    {
      title: "Rubrique / Description",
      dataIndex: "label_name",
      key: "label_name",
      width: 260,
      ellipsis: true,
      render: (text: string, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: "bold" }}>{text}</span>
          {record.description && <span className="shipment-muted">{record.description}</span>}
          {record.account_type_name && (
            <Tag
              color={
                record.account_type_code === "INCOME"
                  ? "green"
                  : record.account_type_code === "EXPENSE"
                  ? "orange"
                  : "blue"
              }
              style={{ fontSize: "12px" }}
            >
              {record.account_type_name}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Quantité",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "right",
      render: (qty: number, record) => (
        <span>{qty ? `${qty} ${record.unit || ""}`.trim() : "-"}</span>
      ),
    },
    {
      title: "Prix Unit.",
      dataIndex: "unit_price",
      key: "unit_price",
      width: 120,
      align: "right",
      render: (price: number) => formatCurrency(price),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "right",
      render: (amount: number, record) => {
        const color =
          record.line_type === CostLineType.REVENUE
            ? token.green8
            : record.line_type === CostLineType.DISBURSEMENT
            ? token.colorPrimary
            : token.orange8;

        return (
          <span style={{ fontWeight: "bold", color }}>
            {formatCurrency(amount)}
          </span>
        );
      },
    },
    {
      title: "Tiers",
      key: "party",
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        if (record.line_type === CostLineType.REVENUE) {
          // Pour les revenus, afficher le client
          const clientName =
            record.client_name && record.client_name !== "null"
              ? record.client_name
              : "-";
          return (
            <div>

              <div
                style={{ fontWeight: clientName !== "-" ? "500" : "normal" }}
              >
                {clientName}
              </div>
            </div>
          );
        } else {
          // Pour les coûts et débours, afficher le fournisseur
          const supplierName =
            record.supplier_name && record.supplier_name !== "null"
              ? record.supplier_name
              : "-";
          return (
            <div>
              <div style={{ fontSize: "12px", color: token.colorTextSecondary }}>
                Fournisseur:
              </div>
              <div
                style={{ fontWeight: supplierName !== "-" ? "500" : "normal" }}
              >
                {supplierName}
              </div>
            </div>
          );
        }
      },
    },
    {
      title: "Facture client",
      key: "billing_status",
      width: 140,
      align: "center",
      render: (_, record) => {
        // Pour les lignes de REVENU (facturation client)
        if (
          record.line_type === CostLineType.REVENUE ||
          record.line_type === CostLineType.DISBURSEMENT
        ) {
          if (record.is_invoiced && record.invoice_id) {
            return (
              <Tooltip title="Cliquer pour voir les détails de la facture">
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, fontWeight: 500, color: token.green8 }}
                  onClick={() => {
                    setSelectedInvoiceId(record.invoice_id!);
                    setInvoiceDetailVisible(true);
                  }}
                >
                  {record.invoice_number || `Facture #${record.invoice_id}`}
                </Button>
              </Tooltip>
            );
          }
          if (record.proforma_invoice_id) {
            return (
              <Tooltip title="Cliquer pour voir la proforma">
                <Space size="small">
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, fontWeight: 500, color: "var(--color-primary)" }}
                    onClick={() => {
                      setSelectedInvoiceId(record.proforma_invoice_id!);
                      setInvoiceDetailVisible(true);
                    }}
                  >
                    {record.proforma_invoice_number || `Proforma #${record.proforma_invoice_id}`}
                  </Button>
                  <Tag color="blue">Proforma</Tag>
                </Space>
              </Tooltip>
            );
          }
          return (
            <Tag color="default" style={{ fontSize: "12px" }}>
              Non facturé
            </Tag>
          );
        }

        return <span>Sans objet</span>;
      },
    },
    {
      title: "Facture fournisseur",
      key: "supplier_billing_status",
      width: 160,
      align: "center",
      render: (_, record) => {
        if (record.line_type === CostLineType.COST || record.line_type === CostLineType.DISBURSEMENT) {
          if (record.is_supplier_billed && record.supplier_bill_number) {
            return (
              <Tooltip title="Cliquer pour voir les détails de la facture fournisseur">
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, fontWeight: 500, color: "var(--color-primary)" }}
                  onClick={() => {
                    setSelectedSupplierBill({
                      id: record.supplier_bill_id,
                      bill_number: record.supplier_bill_number,
                    });
                    setSupplierBillDetailVisible(true);
                  }}
                >
                  {record.supplier_bill_number}
                </Button>
              </Tooltip>
            );
          }
          return (
            <Tag color="default" style={{ fontSize: "12px" }}>
              Non saisi
            </Tag>
          );
        }

        return <span>Sans objet</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      align: "center",
      render: (_, record) => {
        // Bloquer modification/suppression si facturé client OU facturé fournisseur
        const canEdit =
          permissions.canEditCosting &&
          !isCostingLineLocked(record);
        const canDelete =
          permissions.canEditCosting &&
          !isCostingLineLocked(record);

        return (
          <Space size="small">
            {canEdit ? (
              <Tooltip title="Modifier">
                <Button aria-label={`Modifier la ligne ${record.id}`} type="text" icon={<EditOutlined aria-hidden="true" />}
                  size="small"
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  record.is_invoiced
                    ? "Impossible de modifier une ligne facturée client"
                    : "Impossible de modifier une ligne facturée fournisseur"
                }
              >
                <Button
                  type="text"
                  aria-label="Ligne verrouillée" icon={<LockOutlined aria-hidden="true" />}
                  size="small"
                  disabled
                />
              </Tooltip>
            )}

            {canDelete ? (
              <Popconfirm
                title="Supprimer cette ligne ?"
                description="Cette action est irréversible."
                onConfirm={() => handleDelete(record.id)}
                okText="Supprimer"
                cancelText="Annuler"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Supprimer">
                  <Button aria-label={`Supprimer la ligne ${record.id}`} type="text" danger icon={<DeleteOutlined aria-hidden="true" />}
                    size="small"
                  />
                </Tooltip>
              </Popconfirm>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="shipment-panel shipment-costing">
      <ShipmentTabHeading title="Job Costing" icon={<DollarOutlined aria-hidden="true" />} />
      {/* Récapitulatif Financier */}
      <FinancialSummaryCard
        summary={summary}
        loading={loading}
        currencySymbol={currencySymbol}
      />

      {/* Boutons d'action */}
      {permissions.canEditCosting && (
        <div className="shipment-costing-actions">
          <Space wrap>
            <Button
              type="primary"
              icon={<PlusOutlined aria-hidden="true" />}
              onClick={handleCreate}
            >
              Ajouter une ligne
            </Button>

            {/* Bouton Générer Facture - seulement si des lignes revenue/disbursement non facturées existent */}
            {jobCostingLines.some(
              (line) =>
                (line.line_type === CostLineType.REVENUE ||
                  line.line_type === CostLineType.DISBURSEMENT) &&
                !line.is_invoiced
            ) && (
              <Button
                type="default"
                icon={<FileTextOutlined aria-hidden="true" />}
                onClick={() => setInvoiceGeneratorVisible(true)}
              >
                Générer une facture
              </Button>
            )}

            {/* Bouton Saisir une facture fournisseur - seulement si des lignes COST non facturées existent */}
            {jobCostingLines.some(
              (line) =>
                isSupplierBillableLine(line)
            ) && permissions.canCreateSupplierBills && (
              <Button
                type="default"
                icon={<FileTextOutlined aria-hidden="true" />}
                onClick={() => setSupplierBillRecordVisible(true)}
              >
                Saisir une facture fournisseur
              </Button>
            )}
          </Space>
        </div>
      )}

      {/* Info lignes facturées */}
      {jobCostingLines.some((line) => line.is_invoiced) && (
        <Alert
          message="Les lignes facturées ou saisies ne peuvent plus être modifiées ni supprimées."
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <ShipmentDataTable compact columns={columns} rows={jobCostingLines} loading={loading} pageSize={false}
        emptyText={<Empty description="Aucune ligne de costing" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          {permissions.canEditCosting && <Button type="primary" onClick={handleCreate}>Ajouter la première ligne</Button>}
        </Empty>} />

      {/* Modal formulaire ligne costing */}
      <JobCostingLineFormModal
        visible={formModalVisible}
        onClose={handleCloseModal}
        shipmentId={shipmentId}
        line={selectedLine}
        mode={formMode}
      />

      {/* Modal génération facture */}
      <InvoiceGeneratorModal
        visible={invoiceGeneratorVisible}
        onClose={() => setInvoiceGeneratorVisible(false)}
        shipmentId={shipmentId}
        jobCostingLines={jobCostingLines}
        onSuccess={() => {
          setInvoiceGeneratorVisible(false);
          // Rafraîchir les lignes pour afficher les statuts "facturé"
          if (shipmentId) {
            fetchJobCostingByShipment(shipmentId);
            fetchJobCostingSummary(shipmentId);
      onRefreshNeeded?.();
          }
        }}
      />

      {/* Modal saisie facture fournisseur */}
      <SupplierBillRecordModal
        visible={supplierBillRecordVisible}
        onClose={() => setSupplierBillRecordVisible(false)}
        jobCostingLines={jobCostingLines}
        onSuccess={() => {
          setSupplierBillRecordVisible(false);
          // Rafraîchir les lignes pour afficher les statuts "facturé fournisseur"
          if (shipmentId) {
            fetchJobCostingByShipment(shipmentId);
            fetchJobCostingSummary(shipmentId);
      onRefreshNeeded?.();
          }
        }}
      />

      {/* Modal détails facture client */}
      <InvoiceDetailModal
        visible={invoiceDetailVisible}
        invoiceId={selectedInvoiceId}
        onClose={() => {
          setInvoiceDetailVisible(false);
          setSelectedInvoiceId(null);
        }}
        onRefresh={() => {
          if (shipmentId) {
            fetchJobCostingByShipment(shipmentId);
            fetchJobCostingSummary(shipmentId);
      onRefreshNeeded?.();
          }
        }}
      />

      {/* Modal détails facture fournisseur */}
      <SupplierBillDetailModal
        visible={supplierBillDetailVisible}
        bill={selectedSupplierBill}
        onClose={() => {
          setSupplierBillDetailVisible(false);
          setSelectedSupplierBill(null);
        }}
        onRefresh={() => {
          if (shipmentId) {
            fetchJobCostingByShipment(shipmentId);
            fetchJobCostingSummary(shipmentId);
      onRefreshNeeded?.();
          }
        }}
      />
    </div>
  );
};

export default JobCostingTab;
